import { type User as Auth0User } from '@auth0/auth0-spa-js';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { IVerantosUser } from '@verantos-dev/types-common';

import { getAuthClient, resetAuthClient } from './client';
import { getUser, hasAuthParams } from './utils';

interface AuthProviderProps {
  children: React.ReactNode;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  user: IVerantosUser;
  logout: (returnTo?: string) => Promise<void>;
}

// Create typed context
export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  user: {} as IVerantosUser,
  logout: async () => undefined,
});

// Timeout helper for auth operations
const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Operation timed out after ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
};

// Auth operation timeouts (in milliseconds)
const AUTH_TIMEOUT = 15_000; // 15 seconds for initial auth operations
const AUTH_CHECK_TIMEOUT = 10_000; // 10 seconds for periodic checks
const INITIAL_AUTH_CHECK_INTERVAL = 5_000; // Start with 5 seconds between checks
const MAX_AUTH_CHECK_INTERVAL = 30_000; // Max 30 seconds between checks

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Auth0User | undefined>();
  const intl = useIntl();
  const redirectInProgressRef = useRef(false);
  const checkInProgressRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const currentIntervalMsRef = useRef(INITIAL_AUTH_CHECK_INTERVAL);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const initAuth0 = async () => {
      try {
        const auth0Client = await withTimeout(getAuthClient(), AUTH_TIMEOUT);

        if (hasAuthParams()) {
          await withTimeout(auth0Client.handleRedirectCallback(), AUTH_TIMEOUT);
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        const authenticated = await withTimeout(auth0Client.isAuthenticated(), AUTH_TIMEOUT);

        if (!isMounted) return;

        setIsAuthenticated(authenticated);

        if (authenticated) {
          try {
            // Verify token is actually valid before rendering children.
            // isAuthenticated() may return true from localStorage cache even when
            // tokens are expired. getTokenSilently() confirms the token works
            // (refreshing if needed) or throws if the session is truly expired.
            await withTimeout(auth0Client.getTokenSilently(), AUTH_TIMEOUT);
          } catch {
            if (!redirectInProgressRef.current) {
              redirectInProgressRef.current = true;
              console.info('Token verification failed, redirecting to login');
              auth0Client.loginWithRedirect();
            }
            return;
          }

          const userProfile = await withTimeout(auth0Client.getUser(), AUTH_TIMEOUT);
          if (isMounted) {
            setUser(userProfile);
          }
        } else {
          if (!redirectInProgressRef.current) {
            redirectInProgressRef.current = true;
            console.info('User is not authenticated, redirecting to login');
            auth0Client.loginWithRedirect();
          }
        }

        // Only start periodic checks if authenticated
        // This prevents unnecessary checks and redirect loops on slow networks
        if (authenticated && isMounted) {
          const checkAuth = async () => {
            if (checkInProgressRef.current || !isMounted) return;

            checkInProgressRef.current = true;
            try {
              const isAuth = await withTimeout(auth0Client.isAuthenticated(), AUTH_CHECK_TIMEOUT);

              if (!isMounted) return;

              if (!isAuth && !redirectInProgressRef.current) {
                redirectInProgressRef.current = true;
                console.info('User authentication lost, redirecting to login');
                auth0Client.loginWithRedirect();
              } else if (isAuth) {
                // Reset interval to initial value on successful check
                currentIntervalMsRef.current = INITIAL_AUTH_CHECK_INTERVAL;
              }
            } catch (error) {
              // On timeout or error, increase interval with exponential backoff
              console.warn('Auth check failed or timed out:', error);
              if (isMounted) {
                currentIntervalMsRef.current = Math.min(
                  currentIntervalMsRef.current * 2,
                  MAX_AUTH_CHECK_INTERVAL
                );
              }
            } finally {
              checkInProgressRef.current = false;
            }
          };

          const scheduleNextCheck = () => {
            if (!isMounted) return;

            timeoutRef.current = setTimeout(async () => {
              await checkAuth();
              if (isMounted) {
                scheduleNextCheck();
              }
            }, currentIntervalMsRef.current);
          };

          // Check auth immediately when the user returns to the tab
          const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isMounted) {
              checkAuth();
            }
          };
          document.addEventListener('visibilitychange', handleVisibilityChange);
          visibilityCleanupRef.current = () =>
            document.removeEventListener('visibilitychange', handleVisibilityChange);

          // Start the recursive check cycle
          scheduleNextCheck();
        }
      } catch (error) {
        console.error('Error initializing Auth0:', error);

        if (!isMounted) return;

        // If Auth0 initialization fails due to invalid auth parameters (e.g., malformed code/state in the URL such as http://localhost:4200/?code=_emaWVZ9%7C5KcZRUMORKogh42URxaUS0hoe47rtybGuSm&state=dDdBT0NLWHZIVEFENElIZmthNXk1R0d2YzdJdm5GQ350RUhaZ294WXZGeA%253D%253D),
        // redirect to the origin to clear the params and allow a fresh authentication attempt.
        if (!redirectInProgressRef.current) {
          redirectInProgressRef.current = true;
          timeout = setTimeout(() => {
            window.location.href = window.location.origin;
            // timeout to avoid race condition with the redirect
          }, 1000);
        }
      }
    };

    (async () => await initAuth0())();

    return () => {
      isMounted = false;
      clearTimeout(timeoutRef.current);
      clearTimeout(timeout);
      visibilityCleanupRef.current?.();
      redirectInProgressRef.current = false;
      checkInProgressRef.current = false;
    };
  }, []); // Remove isAuthenticated dependency to prevent re-initialization loops

  // Hide children immediately when a 401 is received by any API call.
  // createBaseQuery dispatches this event before triggering the login redirect,
  // ensuring no error UI is visible while the redirect is in progress.
  useEffect(() => {
    const handleAuthInvalidated = () => setUser(undefined);
    window.addEventListener('auth-invalidated', handleAuthInvalidated);
    return () => window.removeEventListener('auth-invalidated', handleAuthInvalidated);
  }, []);

  const logout = async (returnTo: string | undefined) => {
    // Prevent periodic auth checks from triggering loginWithRedirect during logout
    redirectInProgressRef.current = true;
    clearTimeout(timeoutRef.current);

    const auth0Client = await getAuthClient();
    const defaultReturnTo = `${window.location.origin}${import.meta.env.BASE_URL}`;
    await auth0Client.logout({ logoutParams: { returnTo: returnTo || defaultReturnTo } });
    resetAuthClient();
  };

  if (user === undefined) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user: getUser(user, intl), logout }}>
      {children}
    </AuthContext.Provider>
  );
};
